import {ID, Response} from '../../../../../_dashboard/helpers'
import { Theme } from '../../../themes-managment/themes-list/core/_models';
import { Category, City, Facility } from '../../tours-list/core/_models';

export interface images {
  _id: ID;
  url: string;
  seoUrl?: string;
}

export interface ratings {
  _id?: ID;
  title: string;
  star: number;
}

export interface Translation {
  _id?: string;
  tour_id?: string;
  language?: string;
  title?: string;
  description?: string;
  content?: string;
  shortDescription?: string;
  longDescription?: string;
  note?: string;
  cardBadge?: string;
  seoSlug?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  openGraphTitle?: string;
  openGraphDescription?: string;
  openGraphImage?: string;
  openGraphType?: string;
  openGraphLocale?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCardType?: string;
  metaRobots?: string;
  canonicalUrl?: string;
  structuredData?: string;
}

export interface FAQ {
  title: string;
  content: string;
}

export interface IncludeExclude {
  title: string;
  description: string;
}

export interface Itinerary {
  image: string;
  title: string;
  content: string;
}

export interface LocationPoint {
  name: string;
  content: string;
  distance: string;
}

export interface Option {
  _id: string;
  title: string;
}

export interface TourEvent {
  _id?: string;
  title: string;
  content: string;
  description?: string;
  startTime?: string;
  endTime?: string;
  day: string;
  order: number;
  tour?: string;
  image?: images | string; // Can be either full image object or just the ID
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  translations?: Translation[];
}

export interface PersonType {
  _id: string;
  name: string;
  code: string;
  description: string;
  defaultPriceMultiplier: number;
  isActive: boolean;
  minAge: number;
  maxAge: number;
}
export interface PersonTypePrice {
  personTypeId: string;
  price: number;
  priceHT: number;
}

export interface TourPrice {
  _id?: string;
  tour: string;
  priceTTC: number;
  priceHT: number;
  pricingMode: 'PER_PERSON' | 'PER_GROUP';
  personTypePrices: PersonTypePrice[];
  /** Message marketing / mise en avant tarifaire pour le site public (informatif) */
  pricingMarketingNote?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TourType {
  _id: string;
  title: string;
  description?: string;
  status?: string;
  translations?: Array<{
    title: string;
    language: string;
  }>;
}

export interface BookingBlackoutRange {
  from: string;
  to: string;
  type?: 'blocked' | 'custom_request';
  message?: string;
}

export interface TourRouteWaypoint {
  order: number;
  title?: string;
  lat: number;
  lng: number;
}

export interface TourVideo {
  type: 'youtube' | 'vimeo' | 'dailymotion' | 'facebook' | 'tiktok' | 'instagram' | 'mp4' | 'url';
  url: string;
  title?: string;
}

export interface TourDetails {
  id?: ID;
  tour_id?: ID;
  translations: Translation[];
  youtubeVideo?: string;
  videos?: TourVideo[];
  minAdvanceReservations?: string | number;
  duration?: number;
  /** `hours` (default/legacy) or `days` */
  durationUnit?: 'hours' | 'days';
  tourMinPeople?: number;
  tourMaxPeople?: number;
  faqs?: FAQ[];
  includes?: IncludeExclude[];
  excludes?: IncludeExclude[];
  itinerary?: Itinerary[];
  banner?: images;
  bannerImages?: images[];
  gallery?: images[];
  education?: LocationPoint[];
  health?: LocationPoint[];
  transport?: LocationPoint[];
  category?:
    | {
        _id: string;
        translations: { title: string }[];
      }
    | Array<{
        _id: string;
        translations: { title: string }[];
      }>;
  city?: string | {
    _id: string;
    name?: string;
    country?: string;
    region?: string;
  };
  /** Cities covered by itinerary (departure/arrival/stopovers) used for search matching. */
  routeCities?: Array<string | { _id: string; name?: string; country?: string; region?: string }>;
  location?: {
    _id?: string;
    title?: string;
    description?: string;
    mapLatitude?: number;
    mapLongitude?: number;
    mapZoom?: number;
    realAddress?: string;
  } | null;
  facilities?: Facility[];
  options?: Option[];
  themes?: Theme[];
  images?: images[];
  seoKeywords?: string[];
  seoSlug?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoImage?: string;
  phone?: string;
  email?: string;
  price?: number | string;
  promoPrice?: number | string;
  enableFixedDate?: boolean;
  startDate?: string;
  endDate?: string;
  lastBookingDate?: string;
  /** UI-only: days before endDate; persisted as lastBookingDate on save. */
  bookingDeadlineDays?: number | null;
  bookingBlackoutRanges?: BookingBlackoutRange[];
  invalidBookingDateMessage?: string;
  published?: boolean;
  status?: 'draft' | 'published';
  availabilityStatus: string;
  /** When false, tour stays visible but online booking is blocked (default true if omitted). */
  isBookable?: boolean;
  notBookableMessage?: { fr?: string; en?: string; de?: string };
  events: TourEvent[];
  pricing?: TourPrice;
  types?: Array<{
    _id: string;
    title: string;
    translations: { title: string }[];
  }>;
  /** Ordered points for public tour map polyline (straight segments). */
  routeWaypoints?: TourRouteWaypoint[];
  /** Global schedule shown near public map. */
  departureTime?: string;
  /** Global schedule shown near public map. */
  arrivalTime?: string;
  /** Public listing badge color (hex). */
  cardBadgeColor?: string;
}

export type ToursDetailsQueryResponse = Response<Array<TourDetails>>;

export const initialTour: TourDetails = {
  translations: [{
    language: '',
    title: '',
    content: '',
    shortDescription: '',
    longDescription: '',
    note: '',
    cardBadge: '',
  }],
  youtubeVideo: '',
  videos: [],
  minAdvanceReservations: '',
  duration: undefined,
  durationUnit: 'hours',
  tourMinPeople: undefined,
  tourMaxPeople: undefined,
  city: '',
  routeCities: [],
  location: null,
  faqs: [],
  includes: [],
  excludes: [],
  itinerary: [],
  education: [],
  health: [],
  transport: [],
  images: [],
  banner: undefined,
  seoKeywords: [],
  seoSlug: '',
  seoTitle: '',
  seoDescription: '',
  seoImage: '',
  options: [],
  enableFixedDate: undefined,
  startDate: '',
  endDate: '',
  lastBookingDate: '',
  bookingDeadlineDays: null,
  bookingBlackoutRanges: [],
  invalidBookingDateMessage: '',
  published: false,
  category: [],
  availabilityStatus: '',
  isBookable: true,
  notBookableMessage: { fr: '', en: '', de: '' },
  events: [],
  pricing: undefined,
  types: [],
  routeWaypoints: [],
  departureTime: '',
  arrivalTime: '',
  cardBadgeColor: '',
}

export const initialTranslation: Translation = {
  shortDescription: "string",
  longDescription:"string",
  language:"string",
  note:"string",
  content: "",
};